/**
 * Giden Faturalar — portaldan (arşiv) ve UKBA Kasa'dan kesilen tüm faturalar tek tabloda.
 * Sağlayıcı artık tek: Kolaysoft. Ayrım belgenin nereden kesildiğine göre.
 *
 * Kolaysoft satırları kolaysoftFaturalari arşivinden gelir. Okuma maliyetini düşük
 * tutmak için arşivin tamamı indirilmez:
 *   - arama boşsa seçili ay         → month == 'YYYY-MM'
 *   - 10/11 haneli sayı             → recipientId == TCKN/VKN
 *   - MMS/MMD… ile başlıyorsa       → invoiceNumber == fatura no
 *   - diğer (isim)                  → searchTokens array-contains en uzun kelime,
 *                                     kalan kelimeler tarayıcıda süzülür
 * Sistemden gönderilen satırlar şubenin eşleşme kayıtlarından türetilir (sentInvoiceRows.js).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { collection, getCountFromServer, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { iv } from './invoiceTheme';
import InvoicePeriodSelector, { ayEtiketi, currentMonth, sonAylar, yilAylari } from './InvoicePeriodSelector';
import { foldArchiveText, resolveArchiveSearch } from '../../utils/kolaysoftArchiveSearch';
import OfficialInvoiceDialog from './official/OfficialInvoiceDialog';
import { buildOfficialModelForMatch } from '../../utils/officialInvoiceModel';
import {
  buildEdmSentRows,
  filterSentRowsByPlan,
  kolaysoftRowOf,
  mergeSentRows,
  SENT_SOURCE,
  SUBE_ETIKETLERI,
  sortSentRows
} from '../../utils/sentInvoiceRows';
import { getInvoiceStatusMeta } from './invoiceStatusUi';

export const KOLAYSOFT_ARCHIVE_COLLECTION = 'kolaysoftFaturalari';
const PAGE_SIZE = 17;

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDay = (day) => (day ? String(day).slice(0, 10).split('-').reverse().join('.') : '—');


const TAMAMLANDI_SX = { fontWeight: 800, height: 20, bgcolor: '#dcfce7', color: '#166534' };

const statusChip = (row) => {
  if (row.source === SENT_SOURCE.EDM) {
    // Kendi subenin gonderdigi fatura ile arsivden gelen ayni durumdaydi ama
    // "Gonderildi" / "Tamamlandi" diye farkli yaziyordu (02.10.2026): Kolaysoft'a
    // ulasan her fatura tek etiketle gosterilir.
    if (['sent', 'approved'].includes(row.match?.invoiceStatus)) {
      return <Chip size="small" label="Tamamlandı" sx={TAMAMLANDI_SX} />;
    }
    const meta = getInvoiceStatusMeta(row.match?.invoiceStatus);
    return <Chip size="small" color={meta.color} label={meta.label} sx={{ fontWeight: 800, height: 20, ...(meta.sx || {}) }} />;
  }
  if (row.invoiceType === 'IADE') {
    return <Chip size="small" label="İade" sx={{ fontWeight: 800, height: 20, bgcolor: '#fef3c7', color: '#92400e' }} />;
  }
  if (row.succeeded === false) {
    return (
      <span title={row.status}>
        <Chip size="small" color="error" label="Hatalı" sx={{ fontWeight: 800, height: 20 }} />
      </span>
    );
  }
  return <Chip size="small" label="Tamamlandı" sx={TAMAMLANDI_SX} />;
};

const isEfatura = (row) => row.documentType === 'EFATURA' || /TEMEL|TICARI/i.test(String(row.scenario || ''));

const SUBE_RENK = {
  sefakoy_merkez: { bgcolor: '#e0f2fe', color: '#0369a1' },
  sefakoy_sube: { bgcolor: '#ede9fe', color: '#6d28d9' },
  kavakli_sube: { bgcolor: '#ffedd5', color: '#c2410c' }
};

const subeChip = (row) =>
  SUBE_ETIKETLERI[row.sube] ? (
    <Chip size="small" label={SUBE_ETIKETLERI[row.sube]} sx={{ fontWeight: 800, height: 20, ...SUBE_RENK[row.sube] }} />
  ) : (
    <span title="Fatura notunda şube bilgisi yok" style={{ color: iv.textMuted, fontWeight: 700 }}>—</span>
  );

const SUBE_FILTER_ALL = 'all';

const typeChip = (row) =>
  isEfatura(row) ? (
    <Chip size="small" label="e-Fatura" sx={{ fontWeight: 800, height: 20, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
  ) : (
    <Chip size="small" label="e-Arşiv" sx={{ fontWeight: 700, height: 20, bgcolor: '#f1f5f9', color: '#475569' }} />
  );

const DetailRow = ({ label, value }) => (
  <Stack direction="row" spacing={1.5} sx={{ py: 0.45, borderBottom: `1px solid ${iv.border}` }}>
    <Typography sx={{ fontSize: 12.5, color: iv.textMuted, fontWeight: 700, minWidth: 150 }}>{label}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: iv.text, wordBreak: 'break-all' }}>{value || '—'}</Typography>
  </Stack>
);

/**
 * Faturayı resmi GİB düzeninde açar. Fatura bu şubede bir taslakla eşleşmişse
 * (externalInvoice.ettn) taslağın kalemleri kullanılır; yoksa dökümdeki özet.
 */
const loadOfficialModel = async (archiveDoc, sube, settings) => {
  const ext = {
    provider: 'kolaysoft',
    ettn: archiveDoc.ettn,
    invoiceNumber: archiveDoc.invoiceNumber,
    invoiceDate: archiveDoc.invoiceDate,
    total: archiveDoc.total
  };
  let linked = null;
  if (sube) {
    try {
      const snap = await getDocs(
        query(collection(db, 'subeler', sube, 'faturaEslestirmeleri'), where('externalInvoice.ettn', '==', archiveDoc.ettn), limit(1))
      );
      linked = snap.docs[0]?.data() || null;
    } catch (_) {
      linked = null;
    }
  }
  return buildOfficialModelForMatch({
    match: { ...(linked || {}), externalInvoice: linked?.externalInvoice || ext },
    form: linked?.invoiceDraft || {},
    settings: settings || {},
    archiveDoc
  });
};

export default function InvoiceSentPanel({
  sube = '',
  invoiceSettings = null,
  matches = []
}) {
  const [official, setOfficial] = useState({ open: false, model: null, loading: false });

  const openOfficial = async (row) => {
    setOfficial({ open: true, model: null, loading: true });
    const model =
      row.source === SENT_SOURCE.EDM
        ? buildOfficialModelForMatch({ match: row.match, form: row.match.invoiceDraft || {}, settings: invoiceSettings || {} })
        : await loadOfficialModel(row.doc, sube, invoiceSettings);
    setOfficial((prev) => (prev.open ? { open: true, model, loading: false } : prev));
  };

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [month, setMonth] = useState(currentMonth());
  // "Son 6 Ay" / "Tumu" acikken sorgu tek ay yerine ay listesiyle yapilir.
  const [son6, setSon6] = useState(false);
  const [tumYil, setTumYil] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(null);
  const [detail, setDetail] = useState(null);
  const [failedOpen, setFailedOpen] = useState(false);
  const [subeFilter, setSubeFilter] = useState(SUBE_FILTER_ALL);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    getCountFromServer(collection(db, KOLAYSOFT_ARCHIVE_COLLECTION))
      .then((snap) => alive && setTotalCount(snap.data().count))
      .catch(() => alive && setTotalCount(null));
    return () => {
      alive = false;
    };
  }, []);

  const donem = useMemo(() => {
    if (tumYil) return yilAylari(month.slice(0, 4));
    return son6 ? sonAylar(month, 6) : month;
  }, [son6, tumYil, month]);
  const plan = useMemo(() => resolveArchiveSearch(debounced, donem), [debounced, donem]);

  useEffect(() => {
    let alive = true;
    const col = collection(db, KOLAYSOFT_ARCHIVE_COLLECTION);
    const q =
      plan.kind === 'tc'
        ? query(col, where('recipientId', '==', plan.value))
        : plan.kind === 'no'
          ? query(col, where('invoiceNumber', '==', plan.value))
          : plan.kind === 'name'
            ? query(col, where('searchTokens', 'array-contains', plan.value))
            : plan.kind === 'months'
              ? query(col, where('month', 'in', plan.values))
              : query(col, where('month', '==', plan.value));
    setLoading(true);
    setError('');
    getDocs(q)
      .then((snap) => {
        if (!alive) return;
        let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (plan.kind === 'name' && plan.words.length > 1) {
          list = list.filter((r) => {
            const hay = foldArchiveText(r.recipientTitle);
            return plan.words.every((w) => hay.includes(w));
          });
        }
        setRows(list.map(kolaysoftRowOf));
        setPage(0);
      })
      .catch((err) => {
        if (!alive) return;
        setRows([]);
        setError(err?.message || 'Arşiv okunamadı');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [plan]);

  const edmRows = useMemo(() => filterSentRowsByPlan(buildEdmSentRows(matches, sube), plan), [matches, plan, sube]);
  const allRows = useMemo(() => sortSentRows(mergeSentRows(rows, edmRows)), [rows, edmRows]);
  // Sayaclar tekillestirme SONRASI: arsivde de olan Sistem satiri iki kez sayilmasin.
  const subeSayilari = useMemo(() => {
    const c = {};
    allRows.forEach((r) => {
      c[r.sube || ''] = (c[r.sube || ''] || 0) + 1;
    });
    return c;
  }, [allRows]);
  const visibleRows = useMemo(
    () => (subeFilter === SUBE_FILTER_ALL ? allRows : allRows.filter((r) => r.sube === subeFilter)),
    [allRows, subeFilter]
  );
  useEffect(() => setPage(0), [subeFilter, edmRows.length]);

  const sum = useMemo(() => visibleRows.reduce((s, r) => s + (Number(r.total) || 0), 0), [visibleRows]);
  const failedRows = useMemo(() => visibleRows.filter((r) => r.failed), [visibleRows]);
  const failed = failedRows.length;
  const pageRows = visibleRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const planLabel =
    plan.kind === 'tc'
      ? `TCKN/VKN ${plan.value}`
      : plan.kind === 'no'
        ? `Fatura no ${plan.value}`
        : plan.kind === 'name'
          ? `“${debounced.trim()}”`
          : plan.kind === 'months'
            ? tumYil
              ? `${month.slice(0, 4)} yılı`
              : `${ayEtiketi(plan.values[0])} – ${ayEtiketi(plan.values[plan.values.length - 1])}`
            : `${ayEtiketi(plan.value)} ayı`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%', minHeight: 0, p: 1.5 }}>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: iv.text, letterSpacing: '-0.02em' }}>
            Giden Faturalar
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: iv.textMuted, fontWeight: 600 }}>
            Portaldan ve UKBA Kasa'dan kesilen e-Arşiv ve e-Fatura belgeleri
            {totalCount != null ? ` · Kolaysoft arşivinde ${totalCount.toLocaleString('tr-TR')} fatura` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Ad soyad, TCKN/VKN veya fatura no"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setDebounced(search);
            }}
            sx={{ width: 300, '& .MuiInputBase-root': { bgcolor: '#fff', fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: iv.textMuted }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch('')} aria-label="Aramayı temizle">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              ) : null
            }}
          />
        </Stack>
      </Stack>

      <InvoicePeriodSelector
        month={month}
        son6={son6}
        tumYil={tumYil}
        disabled={Boolean(debounced.trim())}
        onMonth={(m, opt) => {
          // Yil degistirirken mod korunur; ay dugmesine basinca tek aya doner.
          if (!opt?.modKoru) {
            setSon6(false);
            setTumYil(false);
          }
          setMonth(m);
        }}
        onSon6={() => {
          setTumYil(false);
          setSon6((v) => !v);
        }}
        onTumYil={() => {
          setSon6(false);
          setTumYil((v) => !v);
        }}
      />

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: iv.text }}>{planLabel}</Typography>
        <Chip size="small" label={`${visibleRows.length.toLocaleString('tr-TR')} fatura`} sx={{ fontWeight: 800 }} />
        <Chip size="small" label={`${formatMoney(sum)} TL`} sx={{ fontWeight: 800, bgcolor: iv.primarySoft, color: iv.primary }} />
        {failed ? (
          <Chip
            size="small"
            color="error"
            label={`${failed} hatalı`}
            onClick={() => setFailedOpen(true)}
            title="Hatalı faturaları listele"
            sx={{ fontWeight: 800, cursor: 'pointer' }}
          />
        ) : null}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={subeFilter}
          onChange={(_, v) => v && setSubeFilter(v)}
          sx={{ ml: 'auto', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.1, py: 0.25 } }}
        >
          <ToggleButton value={SUBE_FILTER_ALL}>Tümü ({allRows.length})</ToggleButton>
          {Object.entries(SUBE_ETIKETLERI).map(([k, etiket]) => (
            <ToggleButton key={k} value={k}>
              {etiket} ({subeSayilari[k] || 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {plan.kind === 'name' ? (
          <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
            İsimde tam kelime aranır (ör. “nursena”, “özçelik”).
          </Typography>
        ) : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: `1px solid ${iv.border}`,
          borderRadius: '10px',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table className="iv-sent-table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Fatura No', 'Şube', 'Tür', 'Tarih', 'Alıcı', 'TCKN / VKN', 'Tutar', 'Durum', ''].map((h, i) => (
                  <TableCell
                    key={h || 'islem'}
                    align={i === 6 ? 'right' : 'left'}
                    sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, bgcolor: '#f8fafc' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: iv.textMuted, fontWeight: 700 }}>
                    Kayıt bulunamadı.
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((r) => (
                  <TableRow
                    className="iv-sent-row"
                    key={r.id}
                    hover
                    onClick={() => (r.source === SENT_SOURCE.EDM ? openOfficial(r) : setDetail(r.doc))}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell className="iv-s-no" sx={{ fontSize: 12.5, fontWeight: 800 }}>{r.invoiceNumber || '—'}</TableCell>
                    <TableCell className="iv-s-kaynak">{subeChip(r)}</TableCell>
                    <TableCell className="iv-s-tur">{typeChip(r)}</TableCell>
                    <TableCell className="iv-s-tarih" sx={{ fontSize: 12.5, fontWeight: 700, color: iv.textMuted }}>{formatDay(r.day)}</TableCell>
                    <TableCell className="iv-s-alici" sx={{ fontSize: 12.5, fontWeight: 700 }}>{r.title}</TableCell>
                    <TableCell className="iv-s-vkn" sx={{ fontSize: 12.5, fontWeight: 600, color: !r.taxId || r.taxId === '11111111111' ? iv.textMuted : iv.text }}>
                      {!r.taxId || r.taxId === '11111111111' ? 'Belirtilmemiş' : r.taxId}
                    </TableCell>
                    <TableCell className="iv-s-tutar" align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: iv.primary }}>
                      {formatMoney(r.total)} TL
                    </TableCell>
                    <TableCell className="iv-s-durum">{statusChip(r.source === SENT_SOURCE.EDM ? r : r.doc)}</TableCell>
                    <TableCell className="iv-s-aksiyon" align="right" sx={{ py: 0.25 }}>
                      <IconButton
                        size="small"
                        title="Faturayı görüntüle / indir"
                        onClick={(e) => {
                          e.stopPropagation();
                          openOfficial(r);
                        }}
                      >
                        <ReceiptLongIcon sx={{ fontSize: 18, color: iv.primary }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={visibleRows.length}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p)}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          sx={{ borderTop: `1px solid ${iv.border}`, flexShrink: 0 }}
        />
      </Box>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, fontSize: 16 }}>{detail?.invoiceNumber}</DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <>
              <DetailRow label="Fatura tarihi" value={formatDay(detail.invoiceDate)} />
              <DetailRow label="Şube" value={SUBE_ETIKETLERI[kolaysoftRowOf(detail).sube] || '—'} />
              <DetailRow
                label="Kolaysoft'ta oluşturulma"
                value={detail.createdAt?.toDate ? detail.createdAt.toDate().toLocaleString('tr-TR') : ''}
              />
              <DetailRow label="Alıcı" value={detail.recipientTitle} />
              <DetailRow
                label="TCKN / VKN"
                value={detail.recipientId === '11111111111' ? 'Belirtilmemiş (11111111111)' : detail.recipientId}
              />
              <DetailRow label="Senaryo / tür" value={`${detail.scenario} · ${detail.invoiceType}`} />
              <DetailRow label="Mal / hizmet tutarı" value={`${formatMoney(detail.goodsTotal)} TL`} />
              <DetailRow label="Vergiler hariç" value={`${formatMoney(detail.taxExclusive)} TL`} />
              <DetailRow label="Vergiler dahil" value={`${formatMoney(detail.taxInclusive)} TL`} />
              <DetailRow label="Fatura toplamı" value={`${formatMoney(detail.total)} TL`} />
              <DetailRow label="Kolaysoft durumu" value={detail.status} />
              {isEfatura(detail) ? (
                <>
                  <DetailRow label="Alıcı GİB etiketi" value={detail.recipientAlias} />
                  <DetailRow label="Gönderici GİB etiketi" value={detail.senderAlias} />
                  <DetailRow label="Alıcı cevabı" value={detail.responseStatus} />
                  <DetailRow label="Zarf UUID" value={detail.envelopeId} />
                </>
              ) : null}
              <DetailRow label="ETTN" value={detail.ettn} />
              <DetailRow label="Kaynak dosya" value={detail.sourceFile} />
              {detail.succeeded === false ? (
                <Alert severity="error" sx={{ mt: 1.25 }}>
                  Bu fatura Kolaysoft'ta başarıyla tamamlanmamış. GİB'e ulaşıp ulaşmadığını Kolaysoft'tan kontrol edin.
                </Alert>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button variant="contained" startIcon={<ReceiptLongIcon />} onClick={() => detail && openOfficial(kolaysoftRowOf(detail))}>
            Faturayı Görüntüle / İndir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failedOpen && failed > 0} onClose={() => setFailedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, fontSize: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
          Hatalı faturalar
          <Chip size="small" color="error" label={failed} sx={{ fontWeight: 800 }} />
          <Typography component="span" sx={{ fontSize: 12.5, color: iv.textMuted, fontWeight: 600, ml: 0.5 }}>
            {planLabel}
          </Typography>
          <IconButton size="small" onClick={() => setFailedOpen(false)} sx={{ ml: 'auto' }} aria-label="Kapat">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Fatura No', 'Tarih', 'Alıcı', 'Tutar', 'Hata'].map((h, i) => (
                  <TableCell key={h} align={i === 3 ? 'right' : 'left'} sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, bgcolor: '#f8fafc' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {failedRows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setFailedOpen(false);
                    if (r.source === SENT_SOURCE.EDM) openOfficial(r);
                    else setDetail(r.doc);
                  }}
                >
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{r.invoiceNumber}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 700, color: iv.textMuted, whiteSpace: 'nowrap' }}>{formatDay(r.day)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{r.title}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: iv.primary, whiteSpace: 'nowrap' }}>
                    {formatMoney(r.total)} TL
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>{r.doc?.status || 'Hatalı'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600, mr: 'auto' }}>
            Satıra tıklayınca faturanın ayrıntısı açılır.
          </Typography>
          <Button onClick={() => setFailedOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <OfficialInvoiceDialog
        open={official.open}
        loading={official.loading}
        model={official.model}
        onClose={() => setOfficial({ open: false, model: null, loading: false })}
      />
    </Box>
  );
}
